import { CALENDAR_KINDS, type CalendarItem, type CalendarKind } from './calendarItems';

// ---------------------------------------------------------------------------
// The calendar's filter lives in the URL — `?show=due,street&q=ranger&week=2`
// — so a journey opened from a chip comes back to the same view, and a view
// can be linked. An absent `show` means every kind; `week` is the offset from
// the current week.
// ---------------------------------------------------------------------------

export interface CalendarFilter {
  /** The kinds shown; empty means all of them. */
  kinds: ReadonlySet<CalendarKind>;
  /** Matched against vendor, release label and fact, case-insensitive. */
  query: string;
  weekOffset: number;
}

const isKind = (value: string): value is CalendarKind => (CALENDAR_KINDS as readonly string[]).includes(value);

/**
 * `universe` is the kinds the calendar offers: every kind on the overview, all
 * but Order by on the warehouse page. A kind outside it is dropped from the URL.
 */
export function readFilter(params: URLSearchParams, universe: readonly CalendarKind[] = CALENDAR_KINDS): CalendarFilter {
  const kinds = new Set((params.get('show') ?? '').split(',').filter(isKind).filter((k) => universe.includes(k)));
  const week = Number.parseInt(params.get('week') ?? '', 10);
  return {
    // Every kind selected is the same view as none selected.
    kinds: kinds.size === universe.length ? new Set() : kinds,
    query: params.get('q') ?? '',
    weekOffset: Number.isFinite(week) ? week : 0,
  };
}

export function writeFilter(params: URLSearchParams, filter: CalendarFilter, universe: readonly CalendarKind[] = CALENDAR_KINDS): URLSearchParams {
  const next = new URLSearchParams(params);
  const kinds = universe.filter((k) => filter.kinds.has(k));
  if (kinds.length > 0 && kinds.length < universe.length) next.set('show', kinds.join(','));
  else next.delete('show');
  if (filter.query.trim()) next.set('q', filter.query);
  else next.delete('q');
  if (filter.weekOffset !== 0) next.set('week', String(filter.weekOffset));
  else next.delete('week');
  return next;
}

/**
 * A plain click shows that kind alone, and clicking the kind already shown
 * alone goes back to all. A modifier click adds or removes one kind, starting
 * from everything when nothing is filtered.
 */
export function pickKind(current: ReadonlySet<CalendarKind>, kind: CalendarKind, combine: boolean, universe: readonly CalendarKind[] = CALENDAR_KINDS): Set<CalendarKind> {
  if (!combine) {
    return current.size === 1 && current.has(kind) ? new Set() : new Set([kind]);
  }
  const next = new Set(current.size === 0 ? universe : current);
  if (next.has(kind)) next.delete(kind); else next.add(kind);
  return next.size === universe.length ? new Set() : next;
}

export function matchesQuery(item: CalendarItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [item.vendor, item.label, item.fact].some((v) => v?.toLowerCase().includes(q));
}

export function kindShown(filter: CalendarFilter, kind: CalendarKind): boolean {
  return filter.kinds.size === 0 || filter.kinds.has(kind);
}
